import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { getStoredUtmParams, clearUtmParams, getChannelFromUtm } from '@/hooks/useUtmTracking';

interface MFAChallenge {
  factorId: string;
}

interface SignInResult {
  error: Error | null;
  mfaRequired?: boolean;
  mfaChallenge?: MFAChallenge;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Save UTM data for a newly registered user
  const saveUtmAttribution = async (userId: string) => {
    const utmParams = getStoredUtmParams();
    if (!utmParams) return;
    
    const channel = getChannelFromUtm(utmParams);
    
    try {
      await supabase.from('user_acquisition_channels').upsert({
        user_id: userId,
        channel,
        source: utmParams.utm_source || null,
        campaign: utmParams.utm_campaign || null,
        medium: utmParams.utm_medium || null,
      }, { onConflict: 'user_id' });
      
      // Clear UTM params after successful save
      clearUtmParams();
    } catch (error) {
      console.error('Failed to save UTM attribution:', error);
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        
        // When user signs up, save UTM attribution
        if (event === 'SIGNED_IN' && session?.user) {
          // Use setTimeout to defer Supabase call
          setTimeout(() => {
            saveUtmAttribution(session.user.id);
          }, 0);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, fullName?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
        }
      }
    });
    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string): Promise<SignInResult> => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    // Check if MFA is required
    if (error && error.message === 'mfa_required') {
      return {
        error: null,
        mfaRequired: true,
        mfaChallenge: undefined,
      };
    }
    
    // Check for MFA in the response
    if (data?.session === null && data?.user === null) {
      // Check the assurance level - if MFA is enrolled, we need to verify
      const { data: factorsData } = await supabase.auth.mfa.listFactors();
      const verifiedFactors = factorsData?.totp?.filter(f => f.status === 'verified') || [];
      
      if (verifiedFactors.length > 0) {
        return {
          error: null,
          mfaRequired: true,
          mfaChallenge: { factorId: verifiedFactors[0].id },
        };
      }
    }
    
    // Log login activity on success
    if (!error && data?.user) {
      try {
        await supabase.rpc('log_user_activity', {
          p_user_id: data.user.id,
          p_activity_type: 'login',
          p_description: 'Signed in to account',
          p_metadata: {},
        });
      } catch (logError) {
        console.error('Failed to log login activity:', logError);
      }
    }
    
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
